import { Box, MenuItem, Typography, Button, Popover, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVideo } from "../../../../context/juno/VideoContext";
import { RowBox } from "../../../../utils/styledBox";
import { getIcon } from "./utils";
import { Info } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

interface ModelVersionSelectBoxProps {
  onChange: (value: string) => void;
}

const ModelVersionSelectBox: React.FC<ModelVersionSelectBoxProps> = ({ onChange }) => {
  const { menu, aiEngine, modelVersion } = useVideo();
  const value = modelVersion[aiEngine];
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? "model-version-popover" : undefined;

  const getLabel = (version: string) => {
    return t(`juno.video.ai.${aiEngine.toLowerCase()}.model.${version}`);
  };

  const modelRestrictions = {
    PIKA: [
      {
        model: "V2_2_SCENES_720P",
        disableMenus: ["t2v", "v2v"],
        fallbackModel: "V2_2_1080P",
      },
      {
        model: "V2_2_SCENES_1080P",
        disableMenus: ["t2v", "v2v"],
        fallbackModel: "V2_2_1080P",
      },
      {
        model: "V2_SWAPS",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "V2_2_1080P",
      },
      {
        model: "V2_ADDITIONS",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "V2_2_1080P",
      },
    ],
    HAILUO: [
      {
        model: "MINI_MAX_REF",
        disableMenus: ["t2v"],
        fallbackModel: "MINI_MAX_LIVE",
      },
    ],
    KLING: [
      {
        model: "V1_6_STANDARD_ELEMENTS",
        disableMenus: ["t2v"],
        fallbackModel: "V1_6_STANDARD",
      },
      {
        model: "V1_6_PRO_ELEMENTS",
        disableMenus: ["t2v"],
        fallbackModel: "V1_6_PRO",
      },
      {
        model: "V2_1_STANDARD",
        disableMenus: ["t2v"],
        fallbackModel: "V2_1_MASTER",
      },
      {
        model: "V2_1_PRO",
        disableMenus: ["t2v"],
        fallbackModel: "V2_1_MASTER",
      },
    ],
    VIDU: [
      {
        model: "VIDU_2_0_720P",
        disableMenus: ["t2v"],
        fallbackModel: "VIDU_Q1",
      },
      {
        model: "VIDU_2_0_1080P",
        disableMenus: ["t2v"],
        fallbackModel: "VIDU_Q1",
      },
      {
        model: "VIDU_2_0_REF_720P",
        disableMenus: ["t2v"],
        fallbackModel: "VIDU_Q1",
      },
      {
        model: "VIDU_2_0_START_END_720P",
        disableMenus: ["t2v"],
        fallbackModel: "VIDU_Q1",
      },
      {
        model: "VIDU_2_0_START_END_1080P",
        disableMenus: ["t2v"],
        fallbackModel: "VIDU_Q1",
      },
      {
        model: "VIDU_Q1_START_END",
        disableMenus: ["t2v"],
        fallbackModel: "VIDU_Q1",
      },
    ],
    WAN: [
      {
        model: "FIRST_TO_LAST",
        disableMenus: ["t2v"],
        fallbackModel: "V2_1",
      },
    ],
    FRAMEPACK: [
      {
        model: "FRAMEPACK_F1",
        disableMenus: ["t2v", "v2v", "upscale", "lipsync"],
        fallbackModel: "FRAMEPACK",
      },
      {
        model: "FRAMEPACK_FIRST_TO_LAST",
        disableMenus: ["t2v", "v2v", "upscale", "lipsync"],
        fallbackModel: "FRAMEPACK",
      },
    ],
    LUMA: [
      {
        model: "RAY_2_REFRAME",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "RAY_2",
      },
      {
        model: "RAY_2_FLASH_REFRAME",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "RAY_2_FLASH",
      },
    ],
    GOOGLE: [
      {
        model: "VEO3",
        disableMenus: ["i2v"],
        fallbackModel: "VEO2",
      },
    ],
    BYTEDANCE: [
      {
        model: "LATENTSYNC",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "SEEDANCE_V1_LITE",
      },
      {
        model: "SEEDANCE_V1_LITE",
        disableMenus: ["lipsync"],
        fallbackModel: "LATENTSYNC",
      },
    ],
  };

  const isDisabled = (version: string): boolean => {
    if (aiEngine === "PIKA" && menu === "v2v") {
      return version !== "V2_SWAPS" && version !== "V2_ADDITIONS";
    }

    if (aiEngine === "LUMA" && menu === "v2v") {
      return version !== "RAY_2_REFRAME" && version !== "RAY_2_FLASH_REFRAME";
    }

    const platformRestrictions = modelRestrictions[aiEngine as keyof typeof modelRestrictions];
    if (platformRestrictions) {
      const restriction = platformRestrictions.find((r) => r.model === version);
      if (restriction && restriction.disableMenus.includes(menu)) {
        return true;
      }
    }

    return false;
  };

  const getDefaultVersion = (currentVersion: string): string => {
    const availableModels = getModelVersions(aiEngine);
    if (!availableModels.includes(currentVersion)) {
      const firstValidModel = availableModels.find((model) => !isDisabled(model));
      if (firstValidModel) {
        return firstValidModel;
      }
    }

    if (aiEngine === "PIKA" && menu === "v2v") {
      if (currentVersion !== "V2_SWAPS" && currentVersion !== "V2_ADDITIONS") {
        const pikaV2vFallback = availableModels.includes("V2_SWAPS") ? "V2_SWAPS" : availableModels[0];
        return pikaV2vFallback || currentVersion;
      }
    } else if (aiEngine === "PIKA" && menu === "i2v") {
      if (currentVersion === "V2_SWAPS" || currentVersion === "V2_ADDITIONS") {
        const pikaI2vFallback = availableModels.includes("V2_2_SCENES_720P") ? "V2_2_SCENES_720P" : availableModels[0];
        return pikaI2vFallback || currentVersion;
      }
    }

    if (aiEngine === "LUMA" && menu === "v2v") {
      if (currentVersion !== "RAY_2_REFRAME" && currentVersion !== "RAY_2_FLASH_REFRAME") {
        const lumaV2vFallback = availableModels.includes("RAY_2_REFRAME") ? "RAY_2_REFRAME" : availableModels[0];
        return lumaV2vFallback || currentVersion;
      }
    } else if (aiEngine === "LUMA" && (menu === "t2v" || menu === "i2v")) {
      if (currentVersion === "RAY_2_REFRAME" || currentVersion === "RAY_2_FLASH_REFRAME") {
        const lumaNonV2vFallback = availableModels.includes("RAY_2") ? "RAY_2" : availableModels[0];
        return lumaNonV2vFallback || currentVersion;
      }
    }

    if (aiEngine === "BYTEDANCE" && menu === "lipsync") {
      if (currentVersion !== "LATENTSYNC") {
        return "LATENTSYNC";
      }
    } else if (aiEngine === "BYTEDANCE" && (menu === "t2v" || menu === "i2v")) {
      if (currentVersion === "LATENTSYNC") {
        return "SEEDANCE_V1_LITE";
      }
    }

    const platformRestrictions = modelRestrictions[aiEngine as keyof typeof modelRestrictions];
    if (platformRestrictions) {
      const restriction = platformRestrictions.find((r) => r.model === currentVersion);
      if (restriction && restriction.disableMenus.includes(menu)) {
        if (availableModels.includes(restriction.fallbackModel) && !isDisabled(restriction.fallbackModel)) {
          return restriction.fallbackModel;
        } else {
          const firstValidModel = availableModels.find((model) => !isDisabled(model));
          if (firstValidModel) return firstValidModel;
        }
      }
    }
    if (availableModels.includes(currentVersion) && !isDisabled(currentVersion)) {
      return currentVersion;
    }

    const firstValidModel = availableModels.find((model) => !isDisabled(model));
    return firstValidModel || availableModels[0] || "";
  };

  useEffect(() => {
    const defaultVersion = getDefaultVersion(value);
    if (defaultVersion !== value) {
      onChange(defaultVersion);
    }
  }, [menu, value, onChange, aiEngine]);

  const renderPopoverItem = (version: string) => {
    const icon = getIcon(version);
    const label = getLabel(version);
    const description = t(`juno.video.ai.${aiEngine.toLowerCase()}.model.${version}_description`);

    return (
      <MenuItem
        value={version}
        selected={value === version}
        disabled={isDisabled(version)}
        onClick={() => handleSelect(version)}
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          flexDirection: "column",
          alignItems: "flex-start",
          border: "1px solid",
          backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.8),
          borderColor: modelVersion[aiEngine] === version ? "primary.main" : "divider",
          borderRadius: 1,
          p: 1.5,
          opacity: isDisabled(version) ? 0.5 : 1,
          pointerEvents: isDisabled(version) ? "none" : "auto",
          transition: "background-color 0.2s, border-color 0.2s",
        }}
      >
        <RowBox sx={{ width: "100%", alignItems: "center", mb: 0.5 }}>
          {icon}
          <Typography
            variant="body1"
            sx={{
              fontWeight: "medium",
              ml: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
          <Box flexGrow={1} />
        </RowBox>
        <Box sx={{ width: "100%", display: "flex", alignItems: "center" }}>
          <Typography
            variant="caption"
            component={"p"}
            sx={{
              color: "text.secondary",
              pl: 4,
              whiteSpace: "pre-wrap",
              minWidth: 0,
              lineHeight: 1.5,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              textAlign: "left",
            }}
          >
            {description}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
      </MenuItem>
    );
  };

  const getModelVersions = (engine: string) => {
    switch (engine) {
      case "PIXVERSE":
        return [
          "V3_5",
          "V3_5_1080P",
          "V3_5_FAST",
          "V3_5_TRANSITION",
          "V3_5_TRANSITION_1080P",
          "V4",
          "V4_1080P",
          "V4_FAST",
          "V4_5",
          "V4_5_1080P",
          "V4_5_FAST",
          "V4_5_TRANSITION",
          "V4_5_TRANSITION_1080P",
        ];
      case "HAILUO":
        return ["MINI_MAX_LIVE", "MINI_MAX_REF", "MINI_MAX_DIRECTOR", "MINI_MAX"];
      case "HAIPER":
        return ["V2_0", "V2_5"];
      case "KLING":
        return [
          "V1_0_STANDARD",
          "V1_0_PRO",
          "V1_5_PRO",
          "V1_6_STANDARD",
          "V1_6_PRO",
          "V1_6_STANDARD_ELEMENTS",
          "V1_6_PRO_ELEMENTS",
          "V2_MASTER",
          "V2_1_STANDARD",
          "V2_1_PRO",
          "V2_1_MASTER",
        ];
      case "LUMA":
        return [
          "RAY_1_6",
          "RAY_2",
          "RAY_2_1080P",
          "RAY_2_4K",
          "RAY_2_FLASH",
          "RAY_2_FLASH_1080P",
          "RAY_2_FLASH_4K",
          "RAY_2_REFRAME",
          "RAY_2_FLASH_REFRAME",
        ];
      case "RUNWAY":
        return ["GEN3_ALPHA_TURBO", "GEN4_TURBO"];
      case "VIDU":
        return [
          "VIDU_2_0_720P",
          "VIDU_2_0_1080P",
          "VIDU_2_0_REF_720P",
          "VIDU_2_0_START_END_720P",
          "VIDU_2_0_START_END_1080P",
          "VIDU_Q1",
          "VIDU_Q1_START_END",
        ];
      case "SYNC":
        return ["LIPSYNC_190", "LIPSYNC_V2"];
      case "BYTEDANCE":
        return ["LATENTSYNC", "SEEDANCE_V1_LITE"];
      case "REALESRGAN":
        return ["REALESRGAN"];
      case "GOOGLE":
        return ["VEO2", "VEO3"];
      case "WAN":
        return ["V2_1", "V2_1_PRO", "FIRST_TO_LAST"];
      case "PIKA":
        return [
          "V2_2_720P",
          "V2_2_1080P",
          "V2_2_SCENES_720P",
          "V2_2_SCENES_1080P",
          "V2_TURBO_720P",
          "V2_SWAPS",
          "V2_ADDITIONS",
        ];
      case "TOPAZLABS":
        return ["TOPAZ_VIDEO_UPSCALE"];
      case "FRAMEPACK":
        return ["FRAMEPACK_F1", "FRAMEPACK", "FRAMEPACK_FIRST_TO_LAST"];
      case "HEDRA":
        return ["CHARACTER3"];
      default:
        return [];
    }
  };

  const selectedIcon = getIcon(value);
  const selectedLabel = getLabel(value);
  const models = getModelVersions(aiEngine);

  return (
    <Grid size={{ xs: 12 }}>
      <Button
        aria-describedby={id}
        variant="outlined"
        onClick={handleClick}
        fullWidth
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          color: "text.primary",
          borderColor: "divider",
          height: 48,
        }}
        endIcon={<Info fontSize="small" sx={{ visibility: "hidden" }} />}
      >
        <RowBox sx={{ alignItems: "center", gap: 1 }}>
          {selectedIcon}
          <Typography variant="body1">{selectedLabel}</Typography>
        </RowBox>
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ ml: 1 }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(15px)",
              minWidth: anchorEl ? anchorEl.clientWidth : 200,
              maxWidth: models.length > 3 ? 700 : 350,
              maxHeight: "80vh",
              overflowY: "auto",
            },
            elevation: 0,
          },
        }}
      >
        <Grid container spacing={1} sx={{ p: 1 }}>
          {models.map((version) => (
            <Grid size={{ xs: models.length > 3 ? 6 : 12 }} key={version}>
              {/* size プロパティを使用 */}
              {renderPopoverItem(version)}
            </Grid>
          ))}
        </Grid>
      </Popover>
    </Grid>
  );
};

export default ModelVersionSelectBox;
